import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useBoards } from '../../hooks';
import { useDocumentTitle } from '../../hooks/core';
import { BoardCard, LoadingSpinner } from '../../components';
import { OnboardingModal } from '../../components/shared/OnboardingModal';
import { hasSeenEducationModal } from '../../lib/educationModals';
import type { Board } from '../../types';

type FilterType = 'all' | 'active' | 'expired' | 'completed';

export const BoardsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { boards, isLoading, error, joinBoard } = useBoards();

  useDocumentTitle('My Boards');
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [joinError, setJoinError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('active');
  const [onboardingOpen, setOnboardingOpen] = useState(() => !hasSeenEducationModal('onboarding'));

  // Split boards into active, expired, and completed
  // Expired = game date > 7 days ago AND not completed
  const { activeBoards, expiredBoards, completedBoards, filteredBoards } = useMemo(() => {
    const active: Board[] = [];
    const expired: Board[] = [];
    const completed: Board[] = [];

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    boards.forEach((board) => {
      if (board.status === 'completed') {
        completed.push(board);
      } else {
        // Check if game date is more than 7 days in the past
        const gameDate = new Date(board.gameDate);
        if (gameDate < sevenDaysAgo) {
          expired.push(board);
        } else {
          active.push(board);
        }
      }
    });

    let filtered: Board[];
    switch (filter) {
      case 'active':
        filtered = active;
        break;
      case 'expired':
        filtered = expired;
        break;
      case 'completed':
        filtered = completed;
        break;
      default:
        filtered = boards;
    }

    return { activeBoards: active, expiredBoards: expired, completedBoards: completed, filteredBoards: filtered };
  }, [boards, filter]);

  const handleFilterChange = (_: React.MouseEvent<HTMLElement>, newFilter: FilterType | null) => {
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  };

  const handleJoin = async () => {
    try {
      setJoinError(null);
      const board = await joinBoard(inviteCode);
      setJoinDialogOpen(false);
      setInviteCode('');
      navigate(`/boards/${board.slug || board.id}`);
    } catch (err) {
      setJoinError(err instanceof Error ? err.message : 'Failed to join board');
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading your boards..." />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          My Boards
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={() => setJoinDialogOpen(true)}>
            Join Board
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/create-board')}
          >
            Create Board
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {boards.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }} data-testid="empty-state">
          <Typography variant="h6" color="text.secondary" gutterBottom>
            You don't have any boards yet
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Create a new board or join an existing one with an invite code
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/create-board')}
          >
            Create Your First Board
          </Button>
        </Box>
      ) : (
        <>
          {/* Filter Toggle */}
          <Box sx={{ mb: 3 }}>
            <ToggleButtonGroup
              value={filter}
              exclusive
              onChange={handleFilterChange}
              size="small"
            >
              <ToggleButton value="all">
                All ({boards.length})
              </ToggleButton>
              <ToggleButton value="active">
                Active ({activeBoards.length})
              </ToggleButton>
              <ToggleButton value="expired">
                Expired ({expiredBoards.length})
              </ToggleButton>
              <ToggleButton value="completed">
                Completed ({completedBoards.length})
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {filteredBoards.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                No {filter === 'completed' ? 'completed' : filter === 'expired' ? 'expired' : 'active'} boards
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {filteredBoards.map((board) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={board.id}>
                  <BoardCard board={board} openSquares={board.openSquares} />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* Join Board Dialog */}
      <Dialog open={joinDialogOpen} onClose={() => setJoinDialogOpen(false)}>
        <DialogTitle>Join a Board</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Join a friend's squares board to participate in their game. The board manager will share an invite code with you — ask them for it if you don't have one yet.
          </Typography>
          {joinError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {joinError}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Invite Code"
            fullWidth
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
            placeholder="e.g., ABC123XY"
            inputProps={{ maxLength: 8 }}
            helperText="The 8-character code shared by the board manager"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJoinDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleJoin} variant="contained" disabled={inviteCode.length !== 8}>
            Join
          </Button>
        </DialogActions>
      </Dialog>

      {/* Onboarding Modal - shows for new users */}
      <OnboardingModal
        open={onboardingOpen}
        onClose={() => setOnboardingOpen(false)}
      />
    </Box>
  );
};
